import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory, getHotArticles } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [categoryArticles, setCategoryArticles] = useState<Article[]>([]);
  const [hotArticles, setHotArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  const category = slug ? categoryMap[slug] : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (slug) {
      setCategoryArticles(getArticlesByCategory(slug));
    }
    setHotArticles(getHotArticles().slice(0, 4));
    setLoading(false);
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <i className="ri-error-warning-line text-5xl text-foreground-600 mb-4"></i>
            <h2 className="text-2xl font-bold text-foreground-300 mb-2">分类不存在</h2>
            <Link to="/" className="text-primary-400 hover:text-primary-300 transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', path: '/' },
            { label: category.name },
          ]} />

          <div className="mb-10">
            <div className="flex items-center gap-4 mb-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-900 border border-background-800/60">
                <i className={`${category.icon} text-2xl text-primary-400`}></i>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground-100" style={{ fontFamily: "var(--font-heading)" }}>
                  {category.name}
                </h1>
                <p className="text-foreground-500 text-sm mt-1">{category.description}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-2/3">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-foreground-200">
                  共 {categoryArticles.length} 篇相关资讯
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-600">按分类筛选：</span>
                  <div className="flex flex-wrap gap-1.5">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                          cat.slug === slug
                            ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                            : 'bg-background-900 text-foreground-500 border border-background-800/60 hover:border-background-700/60 hover:text-foreground-300'
                        }`}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="animate-pulse space-y-6">
                  {[1,2,3].map((i) => (
                    <div key={i} className="h-40 bg-background-800 rounded-xl"></div>
                  ))}
                </div>
              ) : categoryArticles.length === 0 ? (
                <div className="text-center py-16 text-foreground-600">
                  <i className="ri-inbox-line text-4xl mb-3 block"></i>
                  该分类暂无文章
                </div>
              ) : (
                <div className="flex flex-col gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group flex flex-col sm:flex-row gap-5 p-4 rounded-xl bg-background-900 border border-background-800/60 hover:border-background-700/60 transition-all duration-300"
                    >
                      <div className="sm:w-56 shrink-0 aspect-[16/10] sm:aspect-auto rounded-lg overflow-hidden bg-background-800">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-background-800 text-foreground-400">
                            {article.categoryName}
                          </span>
                          {article.isExclusive && (
                            <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-accent-500/20 text-accent-400 border border-accent-500/30">独家</span>
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-foreground-100 group-hover:text-primary-400 transition-colors duration-200 line-clamp-2 leading-snug mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                          {article.title}
                        </h3>
                        <p className="text-foreground-500 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
                        <div className="flex items-center gap-4 text-xs text-foreground-600">
                          <span className="flex items-center gap-1">
                            <i className="ri-user-line"></i>
                            {article.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {article.views.toLocaleString()}
                          </span>
                          <span>{article.publishedAt}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <aside className="lg:w-1/3">
              <div className="sticky top-24">
                <div className="p-5 rounded-xl bg-background-900 border border-background-800/60 mb-6">
                  <h3 className="text-base font-bold text-foreground-200 mb-4 flex items-center gap-2" style={{ fontFamily: "var(--font-heading)" }}>
                    <i className="ri-fire-line text-accent-400"></i>
                    热门文章
                  </h3>
                  <div className="flex flex-col gap-2">
                    {hotArticles.map((article, index) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-background-800/30 transition-colors duration-200 group"
                      >
                        <span className="text-lg font-bold text-foreground-700 shrink-0 w-6 text-center" style={{ fontFamily: "var(--font-heading)" }}>
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm text-foreground-400 group-hover:text-primary-400 transition-colors duration-200 line-clamp-2 leading-snug">
                            {article.title}
                          </p>
                          <span className="text-xs text-foreground-600 mt-1 block">{article.views.toLocaleString()} 阅读</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="p-5 rounded-xl bg-background-900 border border-background-800/60">
                  <h3 className="text-base font-bold text-foreground-200 mb-4 flex items-center gap-2" style={{ fontFamily: "var(--font-heading)" }}>
                    <i className="ri-price-tag-3-line text-primary-400"></i>
                    热门标签
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {['反腐败', '数据隐私', '洗钱', '政商关系', '食品安全', '间谍', '娱乐圈', '金融监管', '暗网'].map((tag) => (
                      <span key={tag} className="px-3 py-1.5 rounded-lg text-xs bg-background-800 text-foreground-500 hover:text-foreground-300 hover:bg-background-700/50 transition-colors duration-200 cursor-pointer">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}